import { useState } from "react";
import { useForm } from "react-hook-form";
import { useHistory } from "react-router-dom";
import { PERMISSIONS } from "@/config";
import { Paths } from "@/common/paths/paths";
import { getString } from "@/common/uistringlabels/ui-string-utils";
import type { IItemVersion } from "@/common/types/item.types";
import type { AddressValidationOutcome } from "@/common/hooks/use-address-validation";
import { useAddressValidation } from "@/common/hooks/use-address-validation";
import { useCreateOrder } from "@/common/hooks/use-create-order";
import {
  checkoutFormDefaultValues,
  type CheckoutFormState,
} from "@/pages/Checkout/checkout-form.types";
import { isDeviceReturnRequired as checkDeviceReturnRequired } from "@/pages/Checkout/checkout-form.util";
import { toCheckoutAddressPatch, toCheckoutValidationErrors } from "@/pages/Checkout/checkout-address-validation.mapper";
import { useSession } from "@/common/providers/session-provider";
import { useUserInfo } from "@/common/providers/user-info-provider";
import { buildCreateOrderRequest } from "@/services/orders/orders.mapper";
import { toAddressValidationRequest } from "@/services/address-validation/address-validation.mapper";
import type {
  AddressFields,
  ValidationAddress,
} from "@/services/address-validation/address-validation.types";

const STRING_PATH = "checkout.formPanel";

type AddressSuggestionOutcome = Extract<AddressValidationOutcome, { status: "suggestionAvailable" }>;

export type AddressSuggestionsModalState = Pick<AddressSuggestionOutcome, "mode" | "suggestion"> & {
  enteredAddress: ValidationAddress;
};

export const useCheckoutFormPanel = (selectedVersion: IItemVersion) => {
  const history = useHistory();
  const amazonianInfo = useUserInfo();
  const { username, hasPermission } = useSession();
  const homeCountry = amazonianInfo?.basicInfo?.addresses?.home?.country ?? "";
  const isDeviceReturnRequired = checkDeviceReturnRequired(homeCountry);
  const form = useForm<CheckoutFormState>({
    defaultValues: {
      ...checkoutFormDefaultValues,
      fullName: `${amazonianInfo?.basicInfo?.firstName ?? ""} ${
        amazonianInfo?.basicInfo?.lastName ?? ""
      }`.trim(),
    },
    mode: "onChange",
    reValidateMode: "onChange",
  });
  const { validateAddress, isValidating } = useAddressValidation();
  const { mutateAsync: createOrder, isPending: isCreatingOrder } = useCreateOrder();
  const [addressSuggestionsModal, setAddressSuggestionsModal] =
    useState<AddressSuggestionsModalState | null>(null);
  const [submissionError, setSubmissionError] = useState<string | null>(null);

  const showSubmitError = () => setSubmissionError(getString(`${STRING_PATH}.submitError`));

  const submitOrder = async (formValues: CheckoutFormState) => {
    try {
      const response = await createOrder(
        buildCreateOrderRequest(formValues, selectedVersion, username ?? "", isDeviceReturnRequired)
      );
      history.push({
        pathname: Paths.confirmation.url,
        state: {
          orderId: response.orderId,
          orderReference: response.orderReference,
          itemType: "hardware",
        },
      });
    } catch {
      showSubmitError();
    }
  };

  const applyOutcome = async (
    outcome: AddressValidationOutcome,
    formValues: CheckoutFormState,
    enteredAddress: ValidationAddress
  ) => {
    form.clearErrors();
    switch (outcome.status) {
      case "proceed":
        setSubmissionError(null);
        await submitOrder(formValues);
        return;
      case "suggestionAvailable":
        setSubmissionError(null);
        setAddressSuggestionsModal({
          mode: outcome.mode,
          suggestion: outcome.suggestion,
          enteredAddress,
        });
        return;
      case "invalid": {
        const { fieldErrors, unattributedMessages } = toCheckoutValidationErrors(outcome.validationErrors);
        Object.entries(fieldErrors).forEach(([field, message]) =>
          form.setError(field as keyof CheckoutFormState, { message })
        );
        const hasFieldErrors = Object.keys(fieldErrors).length > 0;
        if (unattributedMessages.length > 0) {
          setSubmissionError([...new Set(unattributedMessages)].join(" "));
        } else {
          setSubmissionError(hasFieldErrors ? null : getString("addressValidationReasons.addressNotValidated"));
        }
        return;
      }
      case "crossCountryNotAllowed":
        setSubmissionError(getString("addressValidationReasons.crossCountryNotAllowed"));
        return;
    }
  };

  const handleFormSubmit = form.handleSubmit(async (formValues) => {
    if (!hasPermission(PERMISSIONS.USE_ADDRESS_VALIDATION)) {
      setSubmissionError(null);
      await submitOrder(formValues);
      return;
    }

    const validationRequest = toAddressValidationRequest(formValues);
    try {
      const outcome = await validateAddress(validationRequest);
      await applyOutcome(outcome, formValues, validationRequest.address);
    } catch {
      showSubmitError();
    }
  });

  const handleAddressSuggestionsModalClose = () => {
    setAddressSuggestionsModal(null);
  };

  const handleValidAddress = async (address: AddressFields) => {
    setAddressSuggestionsModal(null);
    setSubmissionError(null);
    const patch = toCheckoutAddressPatch(address);
    Object.entries(patch).forEach(([field, value]) =>
      form.setValue(field as keyof CheckoutFormState, value, { shouldDirty: true })
    );
    await submitOrder({ ...form.getValues(), ...patch });
  };

  const handleCancelOrder = (close: () => void) => {
    history.push(Paths.search.url);
    close();
  };

  return {
    addressSuggestionsModal,
    form,
    handleAddressSuggestionsModalClose,
    handleCancelOrder,
    handleFormSubmit,
    handleValidAddress,
    isSubmitting: isCreatingOrder || isValidating,
    isValid: form.formState.isValid,
    submissionError,
  };
};
